import React from 'react';
import AnchorMatchSettings from '../right-panel/AnchorMatchSettings';
import LineItemColumnsPanel from './LineItemColumnsPanel';

const ZonePropertiesPanel = ({
  selectedZone,
  selectedZoneField,
  zoneTextPreview,
  fields,
  zones,
  showAdvanced,
  showAiRefinePanel,
  aiRefineHint,
  aiRefineLoading,
  aiRefineError,
  aiRefineProposal,
  onToggleAiRefinePanel,
  onAiRefineHintChange,
  onAiRefine,
  onAcceptAiProposal,
  onDiscardAiProposal,
  onZoneFieldAssign,
  onZoneAnchorUpdate,
  onZoneDelete,
  onShowFieldForm,
  onSetIdentifierPrefill,
  onSetLeftTab,
  template,
}) => (
  <div className="zone-properties">
    <h4 className="zone-field-name">
      {selectedZone.zone_type === 'line_items_table'
        ? 'Line Items Table'
        : (selectedZoneField?.label || selectedZoneField?.field_key || 'Unmapped Zone')}
    </h4>

    {/* Extracted Text */}
    <div className="zone-text-preview zone-text-prominent">
      <label className="mapping-label">Extracted Text</label>
      {zoneTextPreview === null ? (
        <p className="preview-empty">Select a zone to preview text</p>
      ) : zoneTextPreview.wrongPage ? (
        <p className="preview-empty">Navigate to page {(selectedZone.page_index ?? 0) + 1} to see preview</p>
      ) : !zoneTextPreview.available ? (
        <p className="preview-empty">OCR not available</p>
      ) : zoneTextPreview.wordCount === 0 ? (
        <p className="preview-empty">No text found in this zone. Try repositioning or expanding it.</p>
      ) : (
        <>
          <div className="preview-text preview-text-lg">{zoneTextPreview.text}</div>
          <p className="preview-meta">{zoneTextPreview.wordCount} word{zoneTextPreview.wordCount !== 1 ? 's' : ''} matched</p>
          {selectedZone.zone_type === 'field' && zoneTextPreview.text && (
            <button
              className="btn btn-xs"
              style={{ marginTop: '6px', fontSize: '10px', padding: '2px 8px', background: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd', borderRadius: '4px', cursor: 'pointer' }}
              title="Copy this text to a new identifier on the Identifiers tab"
              onClick={() => {
                onSetIdentifierPrefill(zoneTextPreview.text);
                onSetLeftTab('identifiers');
              }}
            >
              Use as Identifier
            </button>
          )}
        </>
      )}
    </div>

    {/* Field Mapping */}
    {selectedZone.zone_type === 'line_items_table' ? (
      <div className="zone-field-mapping">
        <p className="zone-info-note">This zone defines the table area for line item extraction.</p>
        {template?.id && !String(selectedZone.id).startsWith('temp-') ? (
          <LineItemColumnsPanel templateId={template.id} zoneId={selectedZone.id} />
        ) : (
          <p className="zone-info-note">Save the template to label this table's columns.</p>
        )}
      </div>
    ) : (
      <div className="zone-field-mapping">
        <label className="mapping-label">Mapped Field</label>
        <select
          className="mapping-select"
          value={selectedZone.field_id || ''}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '__create__') {
              onShowFieldForm();
              return;
            }
            if (val) onZoneFieldAssign(selectedZone.id, val);
          }}
        >
          {fields.filter(f => !f.is_line_items).map(f => (
            <option key={f.id} value={f.id}>
              {f.label || f.field_key} ({f.value_type || 'text'})
            </option>
          ))}
          <option value="__create__">+ Create new field…</option>
        </select>
        {selectedZoneField && (
          <p className="mapping-field-info">
            Key: <code>{selectedZoneField.field_key}</code> · Type: {selectedZoneField.value_type || 'text'}
          </p>
        )}
      </div>
    )}

    {/* Zone Type */}
    <div className="zone-type-row">
      <span className="zone-type-label">Zone Type:</span>
      <span className={`zone-type-badge zone-type-${selectedZone.zone_type}`}>
        {selectedZone.zone_type === 'line_items_table' ? 'table' : selectedZone.zone_type}
      </span>
      <span className="zone-page-label">Page {(selectedZone.page_index ?? 0) + 1}</span>
    </div>

    {/* AI Refine */}
    {selectedZoneField && template?.source_document_id && (
      <div className="ai-refine-panel">
        <button
          type="button"
          className="btn-ai-refine"
          onClick={onToggleAiRefinePanel}
        >
          Refine with AI {showAiRefinePanel ? '▲' : '▼'}
        </button>

        {showAiRefinePanel && (
          <div className="ai-refine-content">
            <p className="ai-refine-hint-label">Optional: Tell AI what value to look for</p>
            <input
              type="text"
              className="ai-refine-hint-input"
              placeholder={`e.g. "The total is $1,234.56" or "Vendor is Amazon Business"`}
              value={aiRefineHint}
              onChange={e => onAiRefineHintChange(e.target.value)}
              disabled={aiRefineLoading}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onAiRefine}
              disabled={aiRefineLoading}
            >
              {aiRefineLoading ? 'Analyzing…' : 'Analyze & Propose Zone'}
            </button>

            {aiRefineError && <p className="ai-refine-error">{aiRefineError}</p>}

            {aiRefineProposal && (
              <div className="ai-refine-proposal">
                <p className="ai-refine-captured">
                  <strong>Captured text:</strong> {aiRefineProposal.capturedText || '(none)'}
                </p>
                <p className="ai-refine-reasoning"><em>{aiRefineProposal.reasoning}</em></p>
                <div className="ai-refine-actions">
                  <button type="button" className="btn btn-success btn-sm" onClick={onAcceptAiProposal}>
                    Accept — Apply to Zone
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={onDiscardAiProposal}>
                    Discard
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )}

    {/* Coordinates (Advanced mode) */}
    {showAdvanced && (
      <div className="zone-coords-advanced">
        <label className="mapping-label">Coordinates</label>
        <div className="zone-coords">
          <span>x: {Number(selectedZone.x).toFixed(3)}</span>
          <span>y: {Number(selectedZone.y).toFixed(3)}</span>
          <span>w: {Number(selectedZone.w).toFixed(3)}</span>
          <span>h: {Number(selectedZone.h).toFixed(3)}</span>
        </div>
      </div>
    )}

    {/* Anchor Match Settings */}
    {(selectedZone.zone_type === 'anchor' || selectedZone.zone_type === 'field' || selectedZone.zone_type === 'region') && selectedZone.field_id && (
      <AnchorMatchSettings
        zone={(() => {
          if (selectedZone.zone_type !== 'anchor') {
            const siblingAnchor = zones.find(z => z.field_id === selectedZone.field_id && z.zone_type === 'anchor');
            return siblingAnchor || selectedZone;
          }
          return selectedZone;
        })()}
        ocrTextPreview={zoneTextPreview?.text || ''}
        onUpdate={onZoneAnchorUpdate}
      />
    )}

    {/* Delete */}
    <button
      className="btn btn-danger btn-sm zone-delete-btn"
      onClick={() => onZoneDelete(selectedZone.id)}
      title="Delete this zone"
    >
      Delete Zone
    </button>
  </div>
);

export default ZonePropertiesPanel;
